import {
  formatDistance,
  parseISO
} from 'date-fns'
import i18n from 'i18next'
import { getLocaleConfig } from './locales'
import { memoize } from './cache'

/**
 * Core date formatting functions for golf PWA
 * Optimized for mobile performance and offline usage
 */

type DateInput = string | number | Date | null | undefined

// Ensure date is valid Date object
const ensureDate = (date: DateInput): Date | null => {
  if (!date) return null
  if (date instanceof Date) return date
  if (typeof date === 'string') return parseISO(date)
  if (typeof date === 'number') return new Date(date)
  return null
}

type FormatType = 'short' | 'medium' | 'long' | 'weekday' | 'time' | 'timeSeconds' | 'month' | 'monthYear' | 'dateNumeric' | 'datetime' | 'datetimeSeconds' | 'dayMonth' | 'dayMonthYear'

const _formatDateMemoized = memoize((date: DateInput, formatType: FormatType, locale: string | null): string => {
  const validDate = ensureDate(date)
  if (!validDate || isNaN(validDate.getTime())) return ''

  const config = getLocaleConfig(locale)
  const formatter = config.formatters[formatType] || config.formatters.short

  try {
    return formatter(validDate, config)
  } catch (error) {
    console.warn('Date formatting error:', error)
    // Fallback to ISO string
    return validDate.toISOString().split('T')[0]
  }
})

/**
 * Format date with automatic locale detection
 * Memoized for performance optimization
 */
export const formatDate = (date: DateInput, formatType: FormatType = 'short', locale: string | null = null): string => {
  const resolvedLocale = locale || i18n.language?.split('-')[0] || 'en'
  return _formatDateMemoized(date, formatType, resolvedLocale) as string
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = memoize((date: DateInput, baseDate: DateInput = new Date(), locale: string | null = null): string => {
  const validDate = ensureDate(date)
  const validBaseDate = ensureDate(baseDate)

  if (!validDate || !validBaseDate) return ''

  const config = getLocaleConfig(locale)

  try {
    return formatDistance(validDate, validBaseDate, {
      addSuffix: true,
      locale: config.dateFnsLocale
    })
  } catch (error) {
    console.warn('Relative time formatting error:', error)
    return formatDate(validDate, 'short', locale)
  }
})

/**
 * Parse date string with locale awareness
 */
export const parseDate = (dateString: string | null | undefined, locale: string | null = null): Date | null => {
  if (!dateString) return null

  const config = getLocaleConfig(locale)

  // Try locale-specific parser first
  if (config.parser) {
    const parsed = config.parser(dateString)
    if (parsed && !isNaN(parsed.getTime())) {
      return parsed
    }
  }

  // Fallback to standard parsing
  try {
    return parseISO(dateString)
  } catch (error) {
    console.warn('Date parsing error:', error)
    return null
  }
}

/**
 * Format date for input fields (HTML date input compatible)
 */
export const formatForInput = (date: DateInput): string => {
  const validDate = ensureDate(date)
  if (!validDate) return ''

  const year = validDate.getFullYear()
  const month = String(validDate.getMonth() + 1).padStart(2, '0')
  const day = String(validDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Format datetime for input fields (HTML datetime-local input compatible)
 */
export const formatForInputDateTime = (date: DateInput): string => {
  const validDate = ensureDate(date)
  if (!validDate) return ''

  const year = validDate.getFullYear()
  const month = String(validDate.getMonth() + 1).padStart(2, '0')
  const day = String(validDate.getDate()).padStart(2, '0')
  const hours = String(validDate.getHours()).padStart(2, '0')
  const minutes = String(validDate.getMinutes()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Format time for input fields (HTML time input compatible)
 * Returns HH:mm format (24-hour)
 */
export const formatForInputTime = (date: DateInput): string => {
  const validDate = ensureDate(date)
  if (!validDate) return ''

  const hours = String(validDate.getHours()).padStart(2, '0')
  const minutes = String(validDate.getMinutes()).padStart(2, '0')

  return `${hours}:${minutes}`
}

/**
 * Parse time string (HH:mm) to Date object
 * Uses today's date with the specified time
 */
export const parseTime = (timeString: string | null | undefined): Date | null => {
  if (!timeString) return null

  const [hours, minutes] = timeString.split(':')
  if (!hours || !minutes) return null

  const date = new Date()
  date.setHours(parseInt(hours, 10))
  date.setMinutes(parseInt(minutes, 10))
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}

const _formatDateTimeMemoized = memoize((date: DateInput, locale: string | null): string => {
  const validDate = ensureDate(date)
  if (!validDate || isNaN(validDate.getTime())) return ''

  const config = getLocaleConfig(locale)

  try {
    return config.formatters.datetime(validDate, config)
  } catch (error) {
    console.warn('DateTime formatting error:', error)
    // Fallback to ISO datetime
    return validDate.toISOString().replace('T', ' ').slice(0, 16)
  }
})

/**
 * Format date and time in compact locale-specific format
 * Examples:
 * - Finnish: "12.11.2025 15:15"
 * - English: "11/12/2025 3:15 PM"
 * - Swedish: "2025-11-12 15:15"
 * - Thai: "12/11/2568 15:15"
 */
export const formatDateTime = (date: DateInput, locale: string | null = null): string => {
  const resolvedLocale = locale || i18n.language?.split('-')[0] || 'en'
  return _formatDateTimeMemoized(date, resolvedLocale) as string
}

/**
 * Format time string to H:mm format (e.g., "14:30:00" -> "14:30", "09:00" -> "9:00")
 */
export const formatTimeString = (timeString: string | null | undefined): string => {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':')
  return `${parseInt(hours, 10)}:${minutes}`
}
